/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useQuery } from 'react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type { KeyValueStringString } from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce l’elenco delle azioni possibili per tutte le tipologie di oggetti collegati al task
 * @summary Lista azioni
 */
export const getTipiAzioni = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<KeyValueStringString[]>(
    { url: `/framework-cfg-procedimenti/tipi-azioni`, method: 'get', signal },
    options,
  );
};

export const getGetTipiAzioniQueryKey = () =>
  [`/framework-cfg-procedimenti/tipi-azioni`] as const;

export const getGetTipiAzioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiAzioni>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getTipiAzioni>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiAzioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTipiAzioniQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTipiAzioni>>> = ({
    signal,
  }) => getTipiAzioni(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiAzioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiAzioni>>
>;
export type GetTipiAzioniQueryError = ErrorType<unknown>;

/**
 * @summary Lista azioni
 */
export const useGetTipiAzioni = <
  TData = Awaited<ReturnType<typeof getTipiAzioni>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getTipiAzioni>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiAzioniQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce l’elenco delle azioni possibili per singola tipologia di oggetto collegato al task
 * @summary Lista azioni by tipoOggetto
 */
export const getTipiAzioniTipo = (
  tipo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<KeyValueStringString[]>(
    {
      url: `/framework-cfg-procedimenti/tipi-azioni/${tipo}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiAzioniTipoQueryKey = (tipo: string) =>
  [`/framework-cfg-procedimenti/tipi-azioni/${tipo}`] as const;

export const getGetTipiAzioniTipoQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiAzioniTipo>>,
  TError = ErrorType<unknown>,
>(
  tipo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiAzioniTipo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiAzioniTipo>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTipiAzioniTipoQueryKey(tipo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiAzioniTipo>>
  > = ({ signal }) => getTipiAzioniTipo(tipo, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!tipo, ...queryOptions };
};

export type GetTipiAzioniTipoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiAzioniTipo>>
>;
export type GetTipiAzioniTipoQueryError = ErrorType<unknown>;

/**
 * @summary Lista azioni by tipoOggetto
 */
export const useGetTipiAzioniTipo = <
  TData = Awaited<ReturnType<typeof getTipiAzioniTipo>>,
  TError = ErrorType<unknown>,
>(
  tipo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiAzioniTipo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiAzioniTipoQueryOptions(tipo, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
